/**
 * 管理者管理画面
 *
 * 管理者の一覧・追加・削除
 */

import { useState, useEffect, useCallback } from "react";
import {
  Container,
  Title,
  Stack,
  Paper,
  Table,
  Button,
  Group,
  TextInput,
  PasswordInput,
  Modal,
  Text,
  Badge,
  ActionIcon,
  Alert,
  Loader,
  Center,
} from "@mantine/core";
import { showSuccess, showError } from "../utils/notifications";
import { Admin, fetchAdmins, createAdmin, deleteAdmin } from "../services/adminService";
import { useAuth } from "../hooks/useAuth";
import { Trash2, ShieldCheck } from "lucide-react";
import { formatTimestamp } from "../utils/formatters";

export function AdminsPage() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 追加モーダル
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [adding, setAdding] = useState(false);

  // 削除確認モーダル
  const [deleteTarget, setDeleteTarget] = useState<Admin | null>(null);
  const [deleting, setDeleting] = useState(false);

  // 管理者一覧を取得
  const loadAdmins = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchAdmins();
      setAdmins(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "管理者一覧の取得に失敗しました");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAdmins();
  }, [loadAdmins]);

  // 管理者追加
  const handleAdd = async () => {
    if (!newEmail || !newPassword) return;

    try {
      setAdding(true);
      await createAdmin(newEmail, newPassword);
      showSuccess(`${newEmail} を管理者として追加しました`);
      setAddModalOpen(false);
      setNewEmail("");
      setNewPassword("");
      await loadAdmins();
    } catch (err) {
      showError(err instanceof Error ? err.message : "管理者の追加に失敗しました");
    } finally {
      setAdding(false);
    }
  };

  // 管理者削除
  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      setDeleting(true);
      await deleteAdmin(deleteTarget.uid);
      showSuccess(`${deleteTarget.email} を削除しました`);
      setDeleteTarget(null);
      await loadAdmins();
    } catch (err) {
      showError(err instanceof Error ? err.message : "管理者の削除に失敗しました");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Container size="lg">
      {/* AIアシスタント向けガイド */}
      <div data-ai-guide hidden>
        {`[ページ: 管理者設定]
[概要: 管理者アカウントの一覧表示・追加・削除ができる画面]
[登録管理者数: ${admins.length}名]
[ダイアログ]
- 追加ダイアログ: ${addModalOpen ? '表示中' : '非表示'} (新規管理者登録用)
  - 入力項目: メールアドレス(必須), パスワード(必須・6文字以上)
- 削除確認ダイアログ: ${deleteTarget ? '表示中' : '非表示'}
[操作のヒント]
- 自分自身のアカウントは削除不可
- パスワードはFirebase Authで管理される`}
      </div>

      <Stack gap="lg">
        <Group justify="space-between">
          <Group>
            <ShieldCheck size={32} />
            <Title order={1}>管理者設定</Title>
          </Group>
          <Button onClick={() => setAddModalOpen(true)}>管理者を追加</Button>
        </Group>

        {error && (
          <Alert color="red" title="エラー">
            {error}
          </Alert>
        )}

        <Paper shadow="xs" p="lg" radius="md" withBorder>
          {loading ? (
            <Center py="xl">
              <Loader />
            </Center>
          ) : admins.length === 0 ? (
            <Text c="dimmed" ta="center" py="xl">
              管理者が登録されていません
            </Text>
          ) : (
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>メールアドレス</Table.Th>
                  <Table.Th>作成日時</Table.Th>
                  <Table.Th style={{ width: 100 }}>操作</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {admins.map((admin) => (
                  <Table.Tr key={admin.uid}>
                    <Table.Td>
                      {admin.email}
                      {admin.uid === user?.uid && (
                        <Badge ml="xs" size="xs" color="blue">
                          あなた
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {formatTimestamp(admin.createdAt)}
                    </Table.Td>
                    <Table.Td>
                      {admin.uid !== user?.uid && (
                        <ActionIcon
                          color="red"
                          variant="subtle"
                          onClick={() => setDeleteTarget(admin)}
                        >
                          <Trash2 size={16} />
                        </ActionIcon>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Paper>
      </Stack>

      {/* 追加モーダル */}
      <Modal
        opened={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        title="管理者を追加"
      >
        <Stack gap="md">
          <TextInput
            label="メールアドレス"
            placeholder="admin@example.com"
            required
            value={newEmail}
            onChange={(e) => setNewEmail(e.currentTarget.value)}
          />
          <PasswordInput
            label="パスワード"
            placeholder="6文字以上"
            required
            value={newPassword}
            onChange={(e) => setNewPassword(e.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setAddModalOpen(false)}>
              キャンセル
            </Button>
            <Button onClick={handleAdd} loading={adding}>
              追加
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* 削除確認モーダル */}
      <Modal
        opened={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="管理者を削除"
      >
        <Stack gap="md">
          <Text>
            <strong>{deleteTarget?.email}</strong> を削除しますか？
          </Text>
          <Text size="sm" c="dimmed">
            この操作は取り消せません。
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setDeleteTarget(null)}>
              キャンセル
            </Button>
            <Button color="red" onClick={handleDelete} loading={deleting} leftSection={<Trash2 size={16} />}>
              削除
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
